import {
  Button,
  Group,
  LoadingOverlay,
  Modal,
  Paper,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import { createMEmail, findMEmail, getMEmails } from "../../../api/ModelsApi";
import { IconBook2, IconCopy } from "@tabler/icons-react";
import EmailPreviewer from "../../../components/View/EmailPreviewer";
import { notifications } from "@mantine/notifications";
import { useLanguage } from "../../../stores/LanguageStore";
import BackButton from "../../../components/View/BackButton";
import { useSessionStore } from "../../../stores/UserStore";

const ModelTemplates = ({
  setTableLoading,
  setHighlight,
  inWorkflow,
  onModelCreated,
}) => {
  const lang = useLanguage((s) => s.language); 
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [finding, setFinding] = useState(false);
  const [templateItems, setTemplateItems] = useState([]);
  const reseller = useSessionStore((s) => s.reseller);
  const [selected, setSelected] = useState(null);
  const [fallbackToDefault, setFallbackToDefault] = useState(false);

  const handleOpen = () => {
    setOpened(true);
    setLoading(true);
    setSelected(null);
  };

  const isReseller = useMemo(() => {
    return reseller.name !== "Magileads";
  }, [reseller]);

  useEffect(() => {
    if (loading) {
      if (!isReseller) {
        setLoading(false);
        setTemplateItems(
          [
            lang.model_emails_template.model1,
            lang.model_emails_template.model2,
            lang.model_emails_template.model3,
            lang.model_emails_template.model4,
            lang.model_emails_template.model5,
            lang.model_emails_template.model6,
            lang.model_emails_template.model7,
          ].map((x, index) => ({
            ...x,
            id: index + 1,
            links: [],
            json_template: "",
            is_template: false,
            from_sharing: false,
            sharing: [],
            tags: [],
          }))
        );
      } else {
        getMEmails(true)
          .then((r) => {
            if (r.data.models_list.length > 0) {
              setTemplateItems(r.data.models_list);
            } else {
              setLoading(false);
              setFallbackToDefault(true);
              setTemplateItems(
                [
                  lang.model_emails_template.model1,
                  lang.model_emails_template.model2,
                  lang.model_emails_template.model3,
                  lang.model_emails_template.model4,
                  lang.model_emails_template.model5,
                  lang.model_emails_template.model6,
                  lang.model_emails_template.model7,
                ].map((x, index) => ({
                  ...x,
                  id: index + 1,
                  links: [],
                  json_template: "",
                  is_template: false,
                  from_sharing: false,
                  sharing: [],
                  tags: [],
                }))
              );
            }
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }
  }, [loading, lang]);

  const handleClose = () => {
    setOpened(false);
  };

  const [duplicating, setDuplicating] = useState(false);
  const handleDuplicate = () => {
    setDuplicating(true);

    createMEmail({ ...selected, sharing: [] })
      .then((response) => {
        if (inWorkflow) {
          setDuplicating(true);

          getMEmails()
            .then((res) => {
              let newItemsList = res.data.models_list;
              let newItem = newItemsList.find(
                (x) => x.id === response.data.model_id
              );
              notifications.show({
                title: lang.model_emails.row_highlight.duplicated,
                message: lang.models.created_text,
                color: "tertiary.8",
              });
              onModelCreated?.(newItem);
              handleClose?.();
            })
            .finally(() => {
              setDuplicating(false);
            });
        } else {
          notifications.show({
            message: lang.model_emails.row_highlight.duplicated,
          });
          handleClose();
          setTableLoading?.(true);
          setHighlight?.(response.data.model_id);
          setDuplicating(false);
        }
      })
      .catch(() => setDuplicating(false));
  };

  useEffect(() => {
    if (templateItems.length > 0) {
      setSelected(templateItems[0]);
      if (isReseller && !fallbackToDefault) setFinding(true);
    }
  }, [templateItems, fallbackToDefault, isReseller]);

  useEffect(() => {
    if (finding && selected) {
      findMEmail(selected.id)
        .then((r) => {
          setSelected(r.data.model_profile);
        })
        .finally(() => setFinding(false));
    }
  }, [selected, finding]);

  return (
    <>
      {inWorkflow ? (
        <Tooltip label={lang.model_emails.templates.title}>
          <Button
            variant="subtle"
            size="compact-sm"
            onClick={handleOpen}
            fw={700}
          >
            {lang.model_emails.templates.label}
          </Button>
        </Tooltip>
      ) : (
        <Button
          leftSection={<IconBook2 />}
          size="md"
          radius={"md"}
          fz={16}
          onClick={handleOpen}
          variant="light"
        >
          {lang.model_emails.templates.label}
        </Button>
      )}

      <Modal
        opened={opened}
        onClose={handleClose}
        title={<Title order={5}>{lang.model_emails.templates.title}</Title>}
        size={"80%"}
      >
        <Group
          h={"70vh"}
          w={"100%"}
          wrap="nowrap"
          pos={"relative"}
          align="start"
        >
          <Paper
            component={Stack}
            h={"100%"}
            style={{ overflow: "auto" }}
            shadow="sm"
            w={"30%"}
            px={"sm"}
            gap={"xs"}
            pos={"relative"}
          >
            <LoadingOverlay visible={loading} />

            {templateItems.length === 0 && !loading && (
              <Text size="xl" ta={"center"} c={"dimmed"} w={"100%"} mt={64}>
                {lang.global.no_element_found}
              </Text>
            )}

            {templateItems?.map((item) => (
              <Tooltip label={item.name} key={item.id}>
                <Button
                  key={item.id}
                  variant={selected?.id === item.id ? "light" : "outline"}
                  onClick={() => {
                    setSelected(item);
                    if (isReseller && !fallbackToDefault) {
                      setFinding(true);
                    }
                  }}
                  size="lg"
                >
                  <Text truncate="end" fw={600} size="sm">
                    {item.name}
                  </Text>
                </Button>
              </Tooltip>
            ))}
          </Paper>
          <Stack w={"70%"} h={"100%"} pos={"relative"}>
            <Paper w={"100%"} h={"100%"}>
              {finding ? (
                <LoadingOverlay visible={finding} />
              ) : (
                <EmailPreviewer
                  key={selected?.id}
                  html={selected?.html}
                  subject={selected?.subject}
                  iframeHeight={"calc(70vh - 96px)"}
                />
              )}
            </Paper>
            <Group justify="end">
              <BackButton onClick={handleClose} size="md" />
              <Button
                leftSection={<IconCopy size={18} />}
                onClick={handleDuplicate}
                loading={duplicating}
                size="md"
                disabled={!selected}
              >
                {lang.model_emails.templates.duplicate}
              </Button>
            </Group>
          </Stack>
        </Group>
      </Modal>
    </>
  );
};

export default ModelTemplates;
